import { useState } from 'react';
import { debounce, get } from 'lodash';

import { useQuery } from '@apollo/react-hooks';

import publicAppSearchQuery from './publicAppSearchQuery';

import { OperationVariables } from '@apollo/react-common';

type AppImages = {
  url_128x128: string;
  url_64x64: string;
  url_32x32: string;
  url_16x16: string;
};

export type App = {
  id: string;
  serviceId: string;
  slug: string;
  name: string;
  images: AppImages;
  currentImplementation: {
    id: string;
  };
};

type AppsConnection = {
  edges: Array<App>;
};

export type publicAppSearchQueryResponse = {
  getAppsByNameContains: AppsConnection;
};

export type publicAppSearchQueryVariables = {
  nameContains: string;
  limit: number;
  excludeUpcoming?: boolean;
};

type options = {
  /** limit */
  limit?: number;
  /** searchDebounceDelay */
  searchDebounceDelay?: number;
  /** Excludes upcoming apps from search results */
  excludeUpcoming?: boolean;
};

export type useAppSearchDataOutput = OperationVariables & {
  onSearch: (query: string) => void;
  searchQuery: string;
};

const DEFAULT_LIMIT = 20;

/**
 * Provides the ability to search for Zapier apps (services, integrations) and receive a set of matching results.
 */
const useAppSearchData = ({
  limit,
  searchDebounceDelay,
  excludeUpcoming,
}: options): useAppSearchDataOutput => {
  const [searchQuery, setSearchQuery] = useState('');

  const variables: publicAppSearchQueryVariables = {
    limit: limit || DEFAULT_LIMIT,
    nameContains: searchQuery,
    excludeUpcoming,
  };

  const { data, ...graphqlQueryControls } = useQuery(publicAppSearchQuery, {
    variables,
  });

  const debouncedSetSearchQuery = searchDebounceDelay
    ? debounce(setSearchQuery, searchDebounceDelay)
    : setSearchQuery;

  const apps: App[] = get(data, ['getAppsByNameContains', 'edges'], []);

  return {
    ...graphqlQueryControls,
    // It's weird that this debounce works, but it does. Because it fires the
    // wrapped function on the trailing edge of the debounce delay period,
    // destroying and re-creating the underlying setTimeout with every
    // re-render still results in the correct debounce functionality.
    onSearch: (query: string) => debouncedSetSearchQuery(query),
    searchQuery,
    apps,
  };
};

export default useAppSearchData;
